import React, { useState, useMemo, forwardRef } from 'react'
import {
  FormControl,
  MenuItem,
  ListSubheader,
  InputAdornment,
  TextFieldProps,
  Typography,
  SelectChangeEvent,
  FormHelperText,
  OutlinedInput,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {
  StyledInput,
  StyledInputLabel,
  StyledOptionBox,
  StyledOptionStack,
  StyledSelect,
} from './Dropdown.styled'
import { useFormEditable } from '../../../providers'

export type DropdownOption = {
  value: string
  label: string
  icon?: React.ReactNode
}

type DropdownProps = TextFieldProps & {
  options: DropdownOption[]
  favourites?: { value: string; label: string; icon?: React.ReactNode }[]
  searchable?: boolean
  helperText?: string
  value?: string
  onChange?: (event: SelectChangeEvent<string>) => void
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      options,
      favourites = [],
      helperText,
      searchable = false,
      value,
      onChange,
      size,
      sx,
      disabled,
      ...props
    },
    ref
  ) => {
    const { isEditable } = useFormEditable()
    const [internalValue, setInternalValue] = useState<string>('')
    const [searchText, setSearchText] = useState<string>('')

    const displayedOptions = useMemo(() => {
      if (favourites.length > 0) {
        const favouriteOptions = favourites.filter((fav) =>
          fav.label.toLowerCase().includes(searchText.toLowerCase())
        )
        const nonFavouriteOptions = options.filter(
          (option) =>
            !favourites.some((fav) => fav.value === option.value) &&
            option.label.toLowerCase().includes(searchText.toLowerCase())
        )

        return [
          ...favouriteOptions,
          { value: 'divider', label: '---' },
          ...nonFavouriteOptions,
        ]
      } else {
        return options
      }
    }, [options, favourites, searchText])

    const handleSelectChange = (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value
      if (onChange) {
        onChange(e)
      } else {
        setInternalValue(newValue)
      }
    }

    const currentVal =
      value !== undefined
        ? value
        : internalValue &&
            options.some((option) => option.value === internalValue)
          ? internalValue
          : ''

    return (
      <FormControl fullWidth error={!!helperText}>
        <StyledInputLabel size={size === 'small' ? size : 'normal'}>
          {props.label}
        </StyledInputLabel>
        <StyledSelect
          ref={ref}
          error={!!helperText}
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,
            PaperProps: {
              style: {
                maxHeight: 400,
                overflowY: 'auto',
              },
            },
          }}
          disabled={!isEditable || disabled}
          slotProps={{
            input: {
              readOnly: !isEditable,
            },
          }}
          sx={sx}
          size={size}
          value={currentVal}
          onChange={handleSelectChange}
          onClose={() => setSearchText('')}
          input={<OutlinedInput label={props.label} />}
          renderValue={() => (
            <StyledOptionStack>
              {options.find((option) => option.value === currentVal)?.icon && (
                <StyledOptionBox>
                  {options.find((option) => option.value === currentVal)?.icon}
                </StyledOptionBox>
              )}
              <Typography>
                {options.find((option) => option.value === currentVal)?.label}
              </Typography>
            </StyledOptionStack>
          )}
        >
          {searchable && (
            <ListSubheader>
              <StyledInput
                size="small"
                placeholder="Type to search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation()
                  }
                }}
              />
            </ListSubheader>
          )}
          {displayedOptions.map((option) =>
            option.value === 'divider' ? (
              <ListSubheader key={option.value}>{option.label}</ListSubheader>
            ) : (
              <MenuItem key={option.value} value={option.value}>
                <StyledOptionStack>
                  {option.icon && (
                    <StyledOptionBox>{option.icon}</StyledOptionBox>
                  )}
                  <Typography>{option.label}</Typography>
                </StyledOptionStack>
              </MenuItem>
            )
          )}
        </StyledSelect>
        {helperText && <FormHelperText error>{helperText}</FormHelperText>}
      </FormControl>
    )
  }
)

export default Dropdown
